import dayjs from "dayjs";

export const humanizeDuration = (minutes: number, style?: "short"): string => {
  const hrs = Math.floor(minutes / 60);
  const hrsPlural = hrs > 1 ? "s" : "";
  let hoursText = hrs > 0 ? `hr${hrsPlural}` : "";
  if (style === "short") {
    hoursText = "h";
  }
  const hoursReturn = hrs > 0 ? `${hrs} ${hoursText}` : "";

  const mins = minutes % 60;
  const minsPlural = mins > 1 ? "s" : "";
  let minutesText = mins > 0 ? `min${minsPlural}` : "";
  if (style === "short") {
    minutesText = "m";
  }
  const minutesReturn = mins > 0 ? `${mins} ${minutesText}` : "";

  const separator = hrs > 0 && mins > 0 ? " " : "";
  return `${hoursReturn}${separator}${minutesReturn}`;
};

type ParseTokenResponse = {
  iss: string;
  nbf: number;
  iat: number;
  exp: number;
  aud: string[];
  scope: string[];
  amr: string[];
  client_id: string;
  actp: string;
  sub: string;
  auth_time: number;
  idp: string;
  email: string;
  se: boolean;
  sl: number;
  sp: string;
  st: string;
  "2fa_req": boolean;
  act: Act;
  name: string;
  sid: string;
};
type Act = {
  sub: string;
  email: string;
  amr: string[];
  idp: string;
  iss: string;
};
export const parseToken = (
  token: string | undefined | null
): ParseTokenResponse | null => {
  try {
    if (!token || typeof token !== "string") {
      return null;
    }
    const parts = token.split(".");
    if (parts.length < 2 || !parts[1]) {
      return null;
    }
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const getDefinedProps = <T>(obj: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(obj ?? {}).filter(([, v]) => v !== undefined)
  ) as Partial<T>;

export const camelCaseToSnakeCase = (str: string) =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

export const snakeCaseToCamelCase = (str: string) =>
  str
    .replace(/_[a-z]/g, (letter) => `${letter.toUpperCase()}`)
    .split("_")
    .join("");

export const convertObjectCamelCaseToSnakeCase = <T>(obj: T) => {
  const converted = { ...obj };
  for (const key in obj) {
    // Keys already in snake_case are in `converted` from the spread. Writing
    // them again here let a stale snake_case twin (say a raw `last_updated`
    // left by an earlier fetch), iterated after its camelCase twin
    // (`lastUpdated`, the app's canonical value), overwrite the fresh one.
    if (!key.match(/[A-Z]/g)) continue;
    converted[camelCaseToSnakeCase(key) as keyof T] = obj[key];
    delete converted[key];
  }
  return converted;
};

export const convertObjectSnakeCaseToCamelCase = <T>(obj: T) => {
  const converted = { ...obj };
  for (const key in obj) {
    converted[snakeCaseToCamelCase(key) as keyof T] = obj[key];
    if (key.match(/_[a-z]/g)) {
      delete converted[key];
    }
  }
  return converted;
};

export type CompareProps<D> = {
  originalObject: D;
  newObject: D;
};
type FindSortedIndexProps<D> = {
  array: D[];
  element: D;
  compareFn: (props: CompareProps<D>) => -1 | 0 | 1;
  start?: number;
  end?: number;
};
export const findSortedIndex = <D>({
  array,
  element,
  compareFn,
  start = 0,
  end = array.length
}: FindSortedIndexProps<D>): number => {
  if (array.length === 0) {
    return -1;
  }

  const index = (start + end) >> 1;
  const compareResult = compareFn({
    originalObject: array[index],
    newObject: element
  });

  if (end - start <= 1) {
    return compareResult === 1 ? end : index;
  }

  switch (compareResult) {
    case -1:
      return findSortedIndex({
        array,
        element,
        compareFn,
        start,
        end: index
      });
    case 0:
      return index;
    case 1:
      return findSortedIndex({
        array,
        element,
        compareFn,
        start: index,
        end
      });
  }
};

export const getTimeToNextQuarterHour = (locale = "en-GB") => {
  const nextQuarterHour = new Date();
  nextQuarterHour.setMinutes(
    Math.ceil(nextQuarterHour.getMinutes() / 15) * 15,
    0,
    0
  );

  return nextQuarterHour.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  });
};

export const convertAllDatesToUTC = <T>(params: T) => {
  // Convert all Date params to UTC

  const returnParams = {} as T;
  for (const key in params) {
    const value = params[key];
    if (value instanceof Date) {
      returnParams[key] = convertDateToUTC(value) as any;
    }
  }
  return { ...params, ...returnParams };
};

export const convertDateToUTC = (date: Date) => {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds()
    )
  );
};
// Function to fix TAG ISSUE 1381079 - this will need to be removed once TAG issue is resolved
export const addDateTimeOffset = (date: Date) => {
  const dateToday = dayjs().toDate();
  const offset = dateToday.getTimezoneOffset() * 60000;

  // If the offset is negative, add one day (24 hours in milliseconds)
  // Positive Offset returned from getTimezoneOffset() Indicates that the local time is behind UTC
  let adjustedDate = date;
  if (offset > 0) {
    adjustedDate = new Date(date.getTime() + offset + 60000);
    adjustedDate = new Date(adjustedDate.getTime() + 24 * 60 * 60 * 1000);
  }

  return adjustedDate;
};

export const wait = (timeout: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, timeout);
  });
